// BM-H4482. Late Payment Agent, a Bonsai Pen tool: an unpaid business debt to an approved
// letter before claim and, once linked to a matter, diary dates on that matter. The figures, checks and letter wording are worked out by
// CaseMinder's fixed code (backend/hub/late_payment); this panel collects the facts and shows the
// result. Nothing here sends anything: a solicitor approves and a person sends.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

const IIR_URL = "https://www.insolvencydirect.bis.gov.uk/eiir/";
const today = () => new Date().toISOString().slice(0, 10);
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB") : "");
const gbp = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BLANK = {
  letter_form: "client",
  creditor: { name: "", address: "", email: "", signatory_name: "", signatory_title: "" },
  debtor: { kind: null, name: "", salutation: "", address: "", jurisdiction: null, company_number: "",
            companies_house_status: "", companies_house_checked_at: "", insolvency_check: null, insolvency_checked_at: "" },
  supply_description: "", agreement_description: "",
  client_sold_in_business: null, debtor_bought_in_business: null, disputed: null, construction_contract: null,
  basis: "statutory", contract_rate: "", contract_clause: "", clause_clear: null, s9_rate_accepted_by: null,
  payment_details: "",
  invoices: [{ reference: "", invoice_date: "", due_date: "", amount: "", confirmed: false, extracted: {} }],
};

const STATE_LABEL = {
  intake: "Gathering the facts",
  handed_back: "Stopped: needs a person",
  awaiting_approval: "Draft waiting for a solicitor",
  returned: "Sent back by a solicitor",
  approved: "Approved, ready to send",
  sent: "Sent",
};

const field = "w-full bg-navy-dark border border-navy-border rounded px-2 py-1 text-xs text-txt";

function YesNo({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-2 text-xs text-txt" data-testid={testid}>
      <span>{label}</span>
      <select className={`${field} w-24`} value={value === true ? "yes" : value === false ? "no" : ""}
              onChange={(e) => onChange(e.target.value === "yes" ? true : e.target.value === "no" ? false : null)}>
        <option value="">Not said</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </label>
  );
}

function Text({ label, value, onChange, area, testid, placeholder }) {
  return (
    <label className="block text-xs text-txt-muted">
      {label}
      {area ? (
        <textarea className={field} rows={3} value={value || ""} placeholder={placeholder}
                  onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      ) : (
        <input className={field} value={value || ""} placeholder={placeholder}
               onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      )}
    </label>
  );
}

export default function LatePaymentPanel({ workupId, apiBase = API }) {
  const wid = encodeURIComponent(workupId || "");
  const [workup, setWorkup] = useState(null);
  const [intake, setIntake] = useState(BLANK);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [matterSearch, setMatterSearch] = useState("");
  const [matterResults, setMatterResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [letterDate, setLetterDate] = useState(today());
  const [sentOn, setSentOn] = useState(today());
  const [comments, setComments] = useState("");
  const [chQuery, setChQuery] = useState("");
  const [chMatches, setChMatches] = useState([]);

  const adopt = (w) => {
    setWorkup(w);
    if (w?.intake) setIntake({ ...BLANK, ...w.intake, creditor: { ...BLANK.creditor, ...w.intake.creditor },
                               debtor: { ...BLANK.debtor, ...w.intake.debtor } });
  };

  const load = useCallback(async () => {
    if (!workupId) return;
    try {
      const res = await axios.get(`${apiBase}/late-payment/${wid}`);
      adopt(res.data?.workup || null);
      setLoadError("");
    } catch (e) {
      setLoadError(errorMessage(e, "Could not read this letter."));
    } finally {
      setLoaded(true);
    }
  }, [apiBase, wid, workupId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const set = (path, v) => setIntake((cur) => {
    const next = { ...cur };
    if (path.includes(".")) {
      const [a, b] = path.split(".");
      next[a] = { ...cur[a], [b]: v };
    } else next[path] = v;
    return next;
  });
  const setInvoice = (i, key, v) => setIntake((cur) => ({
    ...cur, invoices: cur.invoices.map((inv, j) => (j === i ? { ...inv, [key]: v, ...(key !== "confirmed" ? { confirmed: false } : {}) } : inv)),
  }));

  const act = async (fn, okMsg, failMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res?.data?.workup !== undefined) adopt(res.data.workup);
      if (okMsg) toast.success(okMsg);
      return res;
    } catch (e) {
      toast.error(errorMessage(e, failMsg));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const save = () => act(() => axios.put(`${apiBase}/late-payment/${wid}/intake`, {
    ...intake, invoices: intake.invoices.filter((i) => i.reference || i.amount || i.due_date)
      .map((i) => ({ ...i, invoice_date: i.invoice_date || null, due_date: i.due_date || null })),
    contract_rate: intake.contract_rate || null,
  }), "Saved.", "Could not save the details.");

  const checkAndDraft = async () => {
    const saved = await save();
    if (!saved) return;
    await act(() => axios.post(`${apiBase}/late-payment/${wid}/draft`, { letter_date: letterDate }), null, "Could not check and draft.");
  };

  const chSearch = () => act(async () => {
    const res = await axios.get(`${apiBase}/late-payment-companies-house`, { params: { q: chQuery } });
    setChMatches(res.data?.matches || []);
    return null;
  }, null, "Companies House search failed.");

  const chPick = (number) => act(async () => {
    const res = await axios.get(`${apiBase}/late-payment-companies-house`, { params: { number } });
    const c = res.data?.company || {};
    setIntake((cur) => ({ ...cur, debtor: { ...cur.debtor, name: c.name, company_number: c.company_number,
      address: c.registered_office, companies_house_status: c.status, companies_house_checked_at: new Date().toISOString(),
      kind: cur.debtor.kind || (c.type === "llp" ? "llp" : "company") } }));
    setChMatches([]);
    return null;
  }, null, "Could not read that company.");

  const link = (ref) => act(() => axios.put(`${apiBase}/late-payment/${wid}/link`, { matter_ref: ref }), ref ? "Linked." : "Unlinked.", "Could not change the matter link.")
    .then(() => { setMatterResults([]); setMatterSearch(""); });

  const findMatter = async (q) => {
    setMatterSearch(q);
    if (q.trim().length < 2) { setMatterResults([]); return; }
    try {
      const res = await axios.get(`${apiBase}/bonsai/filing/matters`, { params: { q, limit: 6 } });
      setMatterResults(Array.isArray(res.data) ? res.data : []);
    } catch {
      setMatterResults([]);
    }
  };

  const download = async () => {
    try {
      const res = await axios.get(`${apiBase}/late-payment/${wid}/letter.docx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Letter before claim ${intake.debtor.name || "debtor"}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errorMessage(e, "Could not download the letter."));
    }
  };

  if (!workupId || !loaded) return null;
  const state = workup?.state || "intake";
  const draft = workup?.draft;
  const calc = workup?.calculation;
  const d = intake.debtor;
  const isCompany = d.kind === "company" || d.kind === "llp";
  const isIndividual = d.kind === "sole_trader" || d.kind === "partnership";

  return (
    <section className="mt-6 bg-white/[0.02] border border-white/10 rounded-lg p-5" data-testid="late-payment-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-txt">Late payment: letter before claim</h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-txt-muted" data-testid="late-payment-state">{STATE_LABEL[state] || state}</span>
        </div>
      </div>
      {loadError && <p className="text-xs text-status-red mt-2">{loadError}</p>}

      <div className="mt-4 space-y-5">
          <p className="text-xs text-txt-muted">
            Business to business debts in England and Wales only. CaseMinder works out the figures and drafts the letter from
            fixed wording; a solicitor approves it and you send it.
          </p>

          <div className="text-xs text-txt" data-testid="lp-matter-link">
            {workup?.matter_ref ? (
              <span>Linked to matter <a className="underline" href={`/matter/${encodeURIComponent(workup.matter_ref)}`}>{workup.matter_ref}</a>
                {state !== "sent" && (
                  <button type="button" className="ml-2 underline text-txt-muted" onClick={() => link("")}>Unlink</button>
                )}
              </span>
            ) : (
              <div className="space-y-1">
                <span className="text-status-amber">Not linked to a matter. Link it before you send, so the dates go in that matter's diary.</span>
                <input className={field} placeholder="Find a matter by name or reference" value={matterSearch}
                       onChange={(e) => findMatter(e.target.value)} data-testid="lp-matter-search" />
                {matterResults.map((m) => (
                  <button key={m.reference || m.matter_reference} type="button" className="block text-left hover:underline"
                          onClick={() => link(m.reference || m.matter_reference)}>
                    {m.matter_title || m.reference} ({m.reference || m.matter_reference})
                  </button>
                ))}
              </div>
            )}
          </div>

          <label className="block text-xs text-txt-muted max-w-sm">Whose letter
            <select className={field} value={intake.letter_form || "client"} onChange={(e) => set("letter_form", e.target.value)}
                    data-testid="lp-letter-form">
              <option value="client">The client's own letter (we draft, they sign)</option>
              <option value="firm">Our letter, on our letterhead</option>
            </select>
          </label>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-txt">Our client (the creditor)</h3>
              <Text label="Name" value={intake.creditor.name} onChange={(v) => set("creditor.name", v)} testid="lp-creditor-name" />
              <Text label="Address" area value={intake.creditor.address} onChange={(v) => set("creditor.address", v)} />
              {intake.letter_form !== "firm" && (
                <>
                  <Text label="Email for replies" value={intake.creditor.email} onChange={(v) => set("creditor.email", v)} />
                  <div className="grid grid-cols-2 gap-2">
                    <Text label="Signed by" value={intake.creditor.signatory_name} onChange={(v) => set("creditor.signatory_name", v)} />
                    <Text label="Their position" value={intake.creditor.signatory_title} onChange={(v) => set("creditor.signatory_title", v)} />
                  </div>
                </>
              )}
            </div>

            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-txt">The debtor</h3>
              <label className="block text-xs text-txt-muted">What they are
                <select className={field} value={d.kind || ""} onChange={(e) => set("debtor.kind", e.target.value || null)} data-testid="lp-debtor-kind">
                  <option value="">Choose</option>
                  <option value="company">Company</option>
                  <option value="llp">LLP</option>
                  <option value="sole_trader">Sole trader</option>
                  <option value="partnership">Partnership</option>
                </select>
              </label>
              {isCompany && (
                <div className="flex gap-2 items-end">
                  <Text label="Find on Companies House" value={chQuery} onChange={setChQuery} placeholder="Name or number" />
                  <Button size="sm" variant="outline" onClick={chSearch} disabled={busy || !chQuery}>Search</Button>
                </div>
              )}
              {chMatches.map((m) => (
                <button key={m.company_number} type="button" onClick={() => chPick(m.company_number)}
                        className="block w-full text-left text-xs text-txt hover:underline">
                  {m.name} ({m.company_number}, {m.status})
                </button>
              ))}
              <Text label="Exact legal name" value={d.name} onChange={(v) => set("debtor.name", v)} testid="lp-debtor-name" />
              {isIndividual && <Text label="Dear ..." value={d.salutation} onChange={(v) => set("debtor.salutation", v)} placeholder="Mr Smith" />}
              <Text label={isCompany ? "Registered office" : "Address"} area value={d.address} onChange={(v) => set("debtor.address", v)} />
              <label className="block text-xs text-txt-muted">Where they are
                <select className={field} value={d.jurisdiction || ""} onChange={(e) => set("debtor.jurisdiction", e.target.value || null)}>
                  <option value="">Choose</option>
                  <option value="england_wales">England and Wales</option>
                  <option value="scotland">Scotland</option>
                  <option value="northern_ireland">Northern Ireland</option>
                  <option value="abroad">Outside the UK</option>
                </select>
              </label>
              {isCompany && (
                <Text label="Companies House status" value={d.companies_house_status}
                      onChange={(v) => set("debtor.companies_house_status", v)} placeholder="active" />
              )}
              {isIndividual && (
                <label className="block text-xs text-txt-muted">
                  <a href={IIR_URL} target="_blank" rel="noreferrer" className="underline">Individual Insolvency Register</a> result
                  {d.kind === "partnership" ? " (search every partner)" : ""}
                  <select className={field} value={d.insolvency_check || ""}
                          onChange={(e) => { set("debtor.insolvency_check", e.target.value || null); set("debtor.insolvency_checked_at", new Date().toISOString()); }}>
                    <option value="">Not searched</option>
                    <option value="clear">Not on the register</option>
                    <option value="bankrupt">Bankrupt</option>
                    <option value="iva">IVA</option>
                    <option value="dro">Debt relief order</option>
                  </select>
                </label>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-txt">The debt</h3>
              <Text label="What was supplied" value={intake.supply_description} onChange={(v) => set("supply_description", v)}
                    placeholder="services we supplied to you: bookkeeping for June to August 2026" />
              <Text label="The agreement (optional)" value={intake.agreement_description} onChange={(v) => set("agreement_description", v)}
                    placeholder="our terms of business dated 1 May 2026" />
              <Text label="How to pay" area value={intake.payment_details} onChange={(v) => set("payment_details", v)}
                    placeholder="by bank transfer to account name ..., sort code ..., account number ..." />
            </div>
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-txt">Checks</h3>
              <YesNo label="Client sold in the course of its business?" value={intake.client_sold_in_business}
                     onChange={(v) => set("client_sold_in_business", v)} />
              <YesNo label="Debtor bought in the course of its business?" value={intake.debtor_bought_in_business}
                     onChange={(v) => set("debtor_bought_in_business", v)} />
              <YesNo label="Is any of it disputed in substance?" value={intake.disputed} onChange={(v) => set("disputed", v)} />
              <YesNo label="Construction contract with payment notices?" value={intake.construction_contract}
                     onChange={(v) => set("construction_contract", v)} />
              <label className="block text-xs text-txt-muted">Interest
                <select className={field} value={intake.basis} onChange={(e) => set("basis", e.target.value)} data-testid="lp-basis">
                  <option value="statutory">Statutory (1998 Act)</option>
                  <option value="contract_fixed">Contract: fixed % a year</option>
                  <option value="contract_above_base">Contract: % above Bank Rate</option>
                </select>
              </label>
              {intake.basis !== "statutory" && (
                <div className="grid grid-cols-2 gap-2">
                  <Text label={intake.basis === "contract_fixed" ? "Rate % a year" : "Margin over Bank Rate %"}
                        value={intake.contract_rate} onChange={(v) => set("contract_rate", v)} />
                  <Text label="Clause" value={intake.contract_clause} onChange={(v) => set("contract_clause", v)} placeholder="7.3" />
                  <div className="col-span-2">
                    <YesNo label="Is the clause clear?" value={intake.clause_clear} onChange={(v) => set("clause_clear", v)} />
                  </div>
                  <label className="col-span-2 flex items-start gap-2 text-xs text-txt" data-testid="lp-s9">
                    <input type="checkbox" checked={!!intake.s9_rate_accepted_by}
                           onChange={(e) => set("s9_rate_accepted_by", e.target.checked ? "me" : null)} />
                    <span>
                      Solicitor only: I have considered sections 8 and 9 of the 1998 Act and this rate is a substantial remedy.
                      {intake.s9_rate_accepted_by && intake.s9_rate_accepted_by !== "me" ? ` Recorded for ${intake.s9_rate_accepted_by}.` : ""}
                    </span>
                  </label>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-txt mb-1">Invoices (amount still owed, including VAT)</h3>
            <table className="w-full text-xs text-txt" data-testid="lp-invoices">
              <thead><tr className="text-txt-muted text-left"><th>Number</th><th>Invoice date</th><th>Due date</th><th>Outstanding £</th><th>Checked</th><th /></tr></thead>
              <tbody>
                {intake.invoices.map((inv, i) => (
                  <tr key={i}>
                    <td><input className={field} value={inv.reference} onChange={(e) => setInvoice(i, "reference", e.target.value)} /></td>
                    <td><input type="date" className={field} value={inv.invoice_date || ""} onChange={(e) => setInvoice(i, "invoice_date", e.target.value)} /></td>
                    <td><input type="date" className={field} value={inv.due_date || ""} onChange={(e) => setInvoice(i, "due_date", e.target.value)} /></td>
                    <td><input className={field} value={inv.amount || ""} onChange={(e) => setInvoice(i, "amount", e.target.value)} /></td>
                    <td className="text-center"><input type="checkbox" checked={!!inv.confirmed} title="I have checked these figures against the invoice"
                                                       onChange={(e) => setInvoice(i, "confirmed", e.target.checked)} /></td>
                    <td><button type="button" className="text-txt-muted" title="Remove"
                                onClick={() => setIntake((cur) => ({ ...cur, invoices: cur.invoices.filter((_, j) => j !== i) }))}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {intake.invoices.length < 20 && (
              <button type="button" className="text-xs underline text-txt-muted mt-1"
                      onClick={() => setIntake((cur) => ({ ...cur, invoices: [...cur.invoices, { ...BLANK.invoices[0] }] }))}>
                Add an invoice
              </button>
            )}
          </div>

          {!["approved", "sent"].includes(state) && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-txt-muted">Letter date
                <input type="date" className={field} value={letterDate} onChange={(e) => setLetterDate(e.target.value)} data-testid="lp-letter-date" />
              </label>
              <Button size="sm" variant="outline" onClick={save} disabled={busy}>Save</Button>
              <Button size="sm" onClick={checkAndDraft} disabled={busy} data-testid="lp-draft">Check and draft</Button>
            </div>
          )}

          {state === "handed_back" && (
            <div className="text-xs text-status-amber" data-testid="lp-stops">
              <p className="font-semibold">Stopped. This needs a person:</p>
              <ul className="list-disc ml-5">{(workup.stops || []).map((s) => <li key={s.code}>{s.reason}</li>)}</ul>
            </div>
          )}

          {state === "returned" && workup.returned && (
            <p className="text-xs text-status-amber">
              Sent back by {workup.returned.name} on {when(workup.returned.at)}: {workup.returned.comments}. Change the details and draft again.
            </p>
          )}

          {calc && draft && (
            <div className="space-y-2" data-testid="lp-draft-view">
              <p className="text-xs text-txt">
                Owed {gbp(calc.totals.amount)}, interest {gbp(calc.totals.interest)}
                {calc.basis === "statutory" ? `, fixed sums ${gbp(calc.totals.fixed_sums)}` : ""}, total {gbp(calc.totals.claimed)};
                then {gbp(calc.totals.daily)} a day. Reply within {draft.reply_days} days
                ({draft.route === "protocol" ? "Debt Claims Protocol" : "Practice Direction"}).
              </p>
              {(calc.warnings || []).includes("rate_tables_not_checked_to_this_date") && (
                <p className="text-xs text-status-amber">
                  The rate tables were last checked on {calc.rate_tables_checked_to}. Check Bank Rate has not changed since.
                </p>
              )}
              <ul className="text-xs text-status-amber list-disc ml-5">{(draft.reminders || []).map((r) => <li key={r}>{r}</li>)}</ul>
              <pre className="whitespace-pre-wrap text-xs text-txt bg-navy-dark p-3 rounded max-h-96 overflow-auto">{draft.text}</pre>

              {state === "awaiting_approval" && (
                <div className="flex flex-wrap items-end gap-2">
                  <Button size="sm" onClick={() => act(() => axios.post(`${apiBase}/late-payment/${wid}/approve`, { version: draft.version }), "Approved.", "Could not approve.")}
                          disabled={busy} data-testid="lp-approve">Approve (solicitor)</Button>
                  <input className={`${field} w-64`} placeholder="What needs changing" value={comments}
                         onChange={(e) => setComments(e.target.value)} />
                  <Button size="sm" variant="outline" disabled={busy || !comments.trim()}
                          onClick={() => act(() => axios.post(`${apiBase}/late-payment/${wid}/return`, { version: draft.version, comments }), "Sent back.", "Could not send back.")}>
                    Send back
                  </Button>
                </div>
              )}

              {(state === "approved" || state === "sent") && workup.approval && (
                <p className="text-xs text-status-green" data-testid="lp-approval">
                  Approved by {workup.approval.name} on {when(workup.approval.at)} (draft {workup.approval.version}).
                </p>
              )}
              {(state === "approved" || state === "sent") && (
                <div className="flex flex-wrap items-end gap-2">
                  <Button size="sm" variant="outline" onClick={download}>Download letter (Word)</Button>
                  {state === "approved" && (
                    <>
                      <label className="text-xs text-txt-muted">Sent on
                        <input type="date" className={field} value={sentOn} onChange={(e) => setSentOn(e.target.value)} />
                      </label>
                      <Button size="sm" onClick={() => act(() => axios.post(`${apiBase}/late-payment/${wid}/sent`, { sent_on: sentOn }), "Recorded and diarised.", "Could not record it.")}
                              disabled={busy} data-testid="lp-sent">I have sent it</Button>
                    </>
                  )}
                </div>
              )}
              {state === "sent" && (
                <div className="text-xs text-txt" data-testid="lp-diary">
                  Sent on {workup.sent?.on} by {workup.sent?.name}. In the diary:
                  <ul className="list-disc ml-5">{(workup.diary || []).map((x) => <li key={x.id}>{x.due_date}: {x.description}</li>)}</ul>
                </div>
              )}
            </div>
          )}
      </div>
    </section>
  );
}
